import * as THREE from 'three';
import { box, M, mat, mesh, sphere } from '../materials';

export interface Dog {
  group: THREE.Group;
  legs: THREE.Group[];
  tail: THREE.Group;
  head: THREE.Group;
}

/** Dog facing +z, origin on the ground. */
export function buildDog(kind: 'pitbull' | 'chihuahua'): Dog {
  const g = new THREE.Group();
  const chi = kind === 'chihuahua';
  const fur = mat(chi ? '#d9a86c' : '#6b6f78', { rough: 1 });
  const s = chi ? 0.42 : 1;
  const legH = (chi ? 0.32 : 0.3) * s;
  const body = box(0.36 * s, 0.32 * s, 0.75 * s, fur, 0, legH + 0.16 * s, 0);
  g.add(body);
  const legs: THREE.Group[] = [];
  for (const x of [-0.12, 0.12])
    for (const z of [-0.27, 0.27]) {
      const leg = new THREE.Group();
      leg.position.set(x * s, legH, z * s);
      leg.add(box(0.1 * s, legH, 0.1 * s, fur, 0, -legH / 2, 0));
      g.add(leg);
      legs.push(leg);
    }
  const head = new THREE.Group();
  head.position.set(0, legH + 0.32 * s, 0.4 * s);
  g.add(head);
  head.add(box(0.32 * s, 0.28 * s, 0.3 * s, fur, 0, 0, 0));
  head.add(box(0.2 * s, 0.14 * s, 0.16 * s, mat(chi ? '#c9955a' : '#5d6068'), 0, -0.05 * s, 0.2 * s));
  head.add(box(0.06 * s, 0.05 * s, 0.03 * s, M.black, 0, -0.01 * s, 0.29 * s));
  for (const x of [-0.08, 0.08])
    head.add(sphere((chi ? 0.05 : 0.035) * s, M.black, x * s, 0.06 * s, 0.15 * s, 6));
  if (chi) {
    for (const x of [-1, 1]) {
      const ear = mesh(new THREE.ConeGeometry(0.09 * s, 0.3 * s, 4), fur, x * 0.13 * s, 0.24 * s, -0.02 * s);
      ear.rotation.z = -x * 0.5;
      head.add(ear);
    }
  } else {
    for (const x of [-1, 1]) head.add(box(0.08, 0.08, 0.04, fur, x * 0.15, 0.13, -0.05));
    g.add(box(0.38, 0.06, 0.1, mat('#b81d24'), 0, legH + 0.3, 0.32));
  }
  const tail = new THREE.Group();
  tail.position.set(0, legH + 0.26 * s, -0.37 * s);
  tail.add(box(0.05 * s, 0.05 * s, 0.28 * s, fur, 0, 0.05 * s, -0.12 * s));
  tail.rotation.x = 0.6;
  g.add(tail);
  g.traverse((o) => (o.userData.noBatch = true));
  return { group: g, legs, tail, head };
}

/** Simple trot/wag animation. */
export function animateDog(dog: Dog, time: number, speed: number, excited = 0): void {
  const run = Math.min(1, speed / 3);
  dog.legs.forEach((leg, i) => {
    leg.rotation.x =
      Math.sin(time * 14 + (i % 2 === 0 ? 0 : Math.PI) + (i > 1 ? Math.PI / 2 : 0)) * 0.7 * run;
  });
  dog.tail.rotation.y = Math.sin(time * (8 + excited * 10)) * (0.4 + excited * 0.4);
  dog.head.rotation.x = Math.sin(time * 2) * 0.05;
}

/** A channel cat, nose along +x. Bigger fish, bigger model. */
export function buildCatfish(lbs: number): THREE.Group {
  const g = new THREE.Group();
  const skin = mat('#5d5a4a', { rough: 0.45 });
  const belly = mat('#d8d2bc', { rough: 0.6 });
  const body = sphere(0.12, skin, 0, 0, 0, 10);
  body.scale.set(2.6, 0.8, 0.9);
  g.add(body);
  const under = sphere(0.1, belly, 0.02, -0.04, 0, 8);
  under.scale.set(2.3, 0.5, 0.75);
  g.add(under);
  const head = box(0.16, 0.11, 0.17, skin, 0.3, 0.0, 0);
  g.add(head);
  const tail = box(0.02, 0.18, 0.14, skin, -0.36, 0.0, 0);
  tail.rotation.z = 0.2;
  g.add(tail);
  for (const s of [-1, 1]) {
    const whisker = box(0.16, 0.008, 0.008, mat('#2a2820'), 0.42, -0.03, s * 0.07);
    whisker.rotation.y = s * 0.5;
    whisker.rotation.z = -0.3;
    g.add(whisker);
  }
  g.add(box(0.12, 0.08, 0.01, skin, -0.02, 0.1, 0));
  g.scale.setScalar(0.8 + Math.min(1.4, lbs / 30));
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

/** A snapping turtle, which you didn't want. */
export function buildTurtle(): THREE.Group {
  const g = new THREE.Group();
  const shell = sphere(0.18, mat('#4a5230', { rough: 0.7 }), 0, 0.04, 0, 10);
  shell.scale.set(1.2, 0.55, 1);
  g.add(shell);
  g.add(box(0.1, 0.07, 0.08, mat('#6b6a45'), 0.24, 0.02, 0));
  for (const x of [-0.12, 0.12])
    for (const z of [-0.14, 0.14]) g.add(box(0.06, 0.04, 0.05, mat('#6b6a45'), x, -0.03, z));
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}
